import React, { FormEvent, useState } from 'react';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useToast } from './Toast';

const MIN_LENGTH = 8;

interface Props {
  /** Se true, mostra aviso de "senha provisória" e o botão secundário faz logout
   *  em vez de "cancelar" — usado no fluxo forçado de primeiro login. */
  forced?: boolean;
  /** Chamado após troca bem-sucedida. Modal usa isso pra fechar; a tela cheia
   *  usa pra redirecionar ao dashboard. */
  onDone?: () => void;
  /** Rótulo do botão secundário. Default: "Cancelar" (ou "Sair" no forced). */
  cancelLabel?: string;
  onCancel?: () => void;
}

export default function ChangePasswordForm({ forced, onDone, cancelLabel, onCancel }: Props) {
  const { reloadMe } = useAuth();
  const { showToast } = useToast();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError('');

    if (!currentPassword || !newPassword || !confirm) {
      setError('Preencha todos os campos.');
      return;
    }
    if (newPassword.length < MIN_LENGTH) {
      setError(`Nova senha muito curta. Use no mínimo ${MIN_LENGTH} caracteres.`);
      return;
    }
    if (newPassword !== confirm) {
      setError('A confirmação não confere com a nova senha.');
      return;
    }
    if (newPassword === currentPassword) {
      setError('A nova senha precisa ser diferente da atual.');
      return;
    }

    setSubmitting(true);
    try {
      await api.put('/auth/password', { currentPassword, newPassword });
      await reloadMe();
      showToast('Senha atualizada com sucesso.', 'success');
      onDone?.();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Não foi possível trocar a senha.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      {forced && (
        <div
          role="status"
          style={{
            background: 'rgba(231, 111, 81, 0.12)',
            border: '1px solid rgba(231, 111, 81, 0.35)',
            color: '#7c2d12',
            padding: '10px 12px',
            borderRadius: 8,
            marginBottom: 16,
            fontSize: 13
          }}
        >
          Sua senha atual é provisória. Defina uma senha pessoal para continuar usando o sistema.
        </div>
      )}

      {error && <p className="msg-error">{error}</p>}

      <div className="field">
        <label htmlFor="cp-current">Senha atual</label>
        <input
          id="cp-current"
          className="input"
          type={showPw ? 'text' : 'password'}
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          autoComplete="current-password"
          autoFocus
          required
        />
      </div>

      <div className="field">
        <label htmlFor="cp-new">Nova senha</label>
        <input
          id="cp-new"
          className="input"
          type={showPw ? 'text' : 'password'}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          autoComplete="new-password"
          minLength={MIN_LENGTH}
          required
        />
        <small style={{ opacity: 0.7 }}>Mínimo {MIN_LENGTH} caracteres.</small>
      </div>

      <div className="field">
        <label htmlFor="cp-confirm">Confirmar nova senha</label>
        <input
          id="cp-confirm"
          className="input"
          type={showPw ? 'text' : 'password'}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          autoComplete="new-password"
          minLength={MIN_LENGTH}
          required
        />
      </div>

      <label
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontSize: 13,
          color: 'var(--text-2)',
          margin: '4px 0 20px',
          textTransform: 'none',
          cursor: 'pointer'
        }}
      >
        <input type="checkbox" checked={showPw} onChange={(e) => setShowPw(e.target.checked)} />
        Mostrar senhas
      </label>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        {onCancel && (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={submitting}
          >
            {cancelLabel || (forced ? 'Sair e usar outra conta' : 'Cancelar')}
          </button>
        )}
        <button type="submit" className="btn btn-primary" disabled={submitting}>
          {submitting ? 'Salvando…' : 'Salvar nova senha'}
        </button>
      </div>
    </form>
  );
}
